// The SE's local benchmark rows, as one module-scope store so the use case pages, the deck step and
// the benchmarks editor all read the same rows. Rows live in `clay-bm-rows-v1` / `clay-bm-seedv`
// and are local to this browser (never autosaved). Loading runs the stored rows through the v3
// migration; retired-lever rows stay in the list as `hidden` (kept, never shown).
//
// Every storage access is guarded: a private window, blocked site data or a bad row must leave the
// app rendering on the seed catalog, and a failed write only loses persistence.

import { create } from 'zustand';
import { readStoredJson, readStoredString, tolerantArray } from '../../_shared/read-stored-json';
import { BmRowV3Schema, BM_SEED_V5, migrateBenchmarkRows, type BmRow } from '../engine/migrate-v3';

export const BM_ROWS_KEY = 'clay-bm-rows-v1';
export const BM_SEED_KEY = 'clay-bm-seedv';

export interface BenchmarkState {
  /** Every row, hidden ones included. */
  rows: BmRow[];
}

function writeRows(rows: BmRow[]): void {
  try {
    localStorage.setItem(BM_ROWS_KEY, JSON.stringify(rows));
    localStorage.setItem(BM_SEED_KEY, BM_SEED_V5);
  } catch {
    /* quota or blocked storage: the edit still applies for this session */
  }
}

/** Seed + stored rows remapped onto use case ids; the seed catalog on any storage failure. */
function readRows(): BmRow[] {
  try {
    const stored = readStoredJson(localStorage, BM_ROWS_KEY, tolerantArray(BmRowV3Schema).nullable(), null);
    const migration = migrateBenchmarkRows(stored, readStoredString(localStorage, BM_SEED_KEY));
    // Rows the SE already had are rewritten once onto use case ids; a pure seed load writes nothing.
    if (stored && migration.changed) writeRows(migration.rows);
    return migration.rows;
  } catch {
    return migrateBenchmarkRows(null, null).rows;
  }
}

export const initialState: BenchmarkState = { rows: migrateBenchmarkRows(null, null).rows };

export const useBenchmarkStore = create<BenchmarkState>(() => ({ rows: readRows() }));

function commit(rows: BmRow[]): void {
  useBenchmarkStore.setState({ rows });
  writeRows(rows);
}

export const benchmarkActions = {
  patchRow(id: string, patch: Partial<Omit<BmRow, 'id'>>): void {
    commit(useBenchmarkStore.getState().rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  },
  addRow(row: BmRow): void {
    commit([...useBenchmarkStore.getState().rows, row]);
  },
  appendRows(rows: BmRow[]): void {
    commit([...useBenchmarkStore.getState().rows, ...rows]);
  },
  deleteRow(id: string): void {
    commit(useBenchmarkStore.getState().rows.filter((r) => r.id !== id));
  },
};

/** All rows, hidden included: for callers that read once (the deck snapshot). */
export function loadBenchmarkRows(): BmRow[] {
  return useBenchmarkStore.getState().rows;
}
